import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { getAuthToken } from '@/services/api';
import { hasActiveSubscription } from '@/composables/useTenant';
import DashboardLayout from '@/layouts/DashboardLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';

// Page Views
import DashboardView from '@/views/DashboardView.vue';
import TeamsView from '@/views/TeamsView.vue';
import RosterView from '@/views/RosterView.vue';
import LineupView from '@/views/LineupView.vue';
import LiveGameView from '@/views/LiveGameView.vue';
import VideoAnalyticsView from '@/views/VideoAnalyticsView.vue';
import TrainingView from '@/views/TrainingView.vue';
import RecruitingView from '@/views/RecruitingView.vue';
import MasterManagementView from '@/views/MasterManagementView.vue';
import EventsView from '@/views/EventsView.vue';
import BillingView from '@/views/BillingView.vue';
import SettingsView from '@/views/SettingsView.vue';
import OnboardingView from '@/views/OnboardingView.vue';
import LoginView from '@/views/LoginView.vue';
import TenantRegistrationView from '@/views/TenantRegistrationView.vue';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: LoginView,
      },
    ],
  },
  {
    path: '/onboarding',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Onboarding',
        component: OnboardingView,
      },
    ],
  },
  {
    path: '/register-tenant',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'RegisterTenant',
        component: TenantRegistrationView,
      },
    ],
  },
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true, requiresOrg: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: DashboardView,
      },
      {
        path: 'teams',
        name: 'Teams',
        component: TeamsView,
      },
      {
        path: 'teams/:id/roster',
        name: 'TeamRoster',
        component: RosterView,
      },
      {
        path: 'master-management',
        name: 'MasterManagement',
        component: MasterManagementView,
      },
      {
        path: 'lineup',
        name: 'Lineup',
        component: LineupView,
      },
      {
        path: 'live-game',
        name: 'LiveGame',
        component: LiveGameView,
      },
      {
        path: 'video-analytics',
        name: 'VideoAnalytics',
        component: VideoAnalyticsView,
      },
      {
        path: 'training',
        name: 'Training',
        component: TrainingView,
      },
      {
        path: 'recruiting',
        name: 'Recruiting',
        component: RecruitingView,
      },
      {
        path: 'events',
        name: 'Events',
        component: EventsView,
      },
      {
        path: 'billing',
        name: 'Billing',
        component: BillingView,
      },
      {
        path: 'settings',
        name: 'Settings',
        component: SettingsView,
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Navigation Guard: Enforce authentication and active subscription for protected dashboard routes
router.beforeEach((to, from, next) => {
  const token = getAuthToken();
  const hasClerkSession = typeof window !== 'undefined' && Boolean((window as any).Clerk?.session && (window as any).Clerk?.user);
  const isAuthenticated = Boolean((token && !token.startsWith('bearer_session_')) || hasClerkSession);

  const isAuthRoute = to.path.startsWith('/auth') || to.path === '/register-tenant';

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      console.warn('[Router Guard] Protected route accessed without active authentication. Redirecting to /auth/login...');
      next({ path: '/auth/login', query: { redirect: to.fullPath } });
      return;
    }

    // Check tenant active subscription status
    const isSubscriptionExempt = to.path.startsWith('/dashboard/billing') || to.path.startsWith('/dashboard/settings');
    if (!hasActiveSubscription.value && !isSubscriptionExempt) {
      console.info('[Router Guard] No active tenant subscription. Redirecting to billing page...');
      next({ path: '/dashboard/billing', query: { reason: 'subscription_required' } });
      return;
    }
  } else if (isAuthRoute && isAuthenticated && to.path === '/auth/login') {
    if (!hasActiveSubscription.value) {
      next('/dashboard/billing');
    } else {
      next('/dashboard');
    }
    return;
  }

  next();
});

export default router;